/**
 * Single contribution card used in ContributorDashboard and DashboardSubmissions.
 * Receives all display and action props from parent.
 */
function ContributionCard({
  contrib,
  repoSlug,
  user,
  showApprovedMessage,
  formatDate,
  getSubmissionTypeLabel,
  getStatusBadgeClass,
  onEdit,
  onApprove,
  onReject,
  onCardClick,
  isProcessing,
  managedRepositoryIds,
}) {
  const isApprovedAndClickable = contrib.status === 'approved' && repoSlug

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.contribution-status-badge')) {
      return
    }
    if (isApprovedAndClickable && onCardClick) {
      onCardClick(repoSlug)
    }
  }

  return (
    <div
      className={`contribution-item ${isApprovedAndClickable ? 'contribution-item-clickable' : ''}`}
      onClick={isApprovedAndClickable ? handleCardClick : undefined}
      style={isApprovedAndClickable ? { cursor: 'pointer' } : {}}
    >
      <div className="contribution-item-header">
        <div className="contribution-item-info">
          {repoSlug ? (
            <span className="contribution-item-title">
              Contribution to <strong>{repoSlug}</strong>
              {['new_commit', 'circuit_notes', 'new_issue'].includes(contrib.submission_type) && (
                <span className="contribution-item-type"> · {getSubmissionTypeLabel(contrib.submission_type)}</span>
              )}
            </span>
          ) : contrib.submission_type === 'new_repository' && contrib.metadata?.title ? (
            <span className="contribution-item-title">
              <strong>{contrib.metadata.title}</strong>
              <span className="contribution-item-type"> · New repository</span>
            </span>
          ) : contrib.submission_type === 'new_repository' ? (
            <span className="contribution-item-title">
              <strong>New repository</strong>
            </span>
          ) : contrib.repository_id ? (
            <span className="contribution-item-title">
              Contribution to repository
              <span className="contribution-item-type"> · {getSubmissionTypeLabel(contrib.submission_type)}</span>
            </span>
          ) : (
            <span className="contribution-item-title">
              {getSubmissionTypeLabel(contrib.submission_type)}
            </span>
          )}
        </div>
        <span className={`contribution-status-badge ${getStatusBadgeClass(contrib.status)}`}>
          {contrib.status}
        </span>
      </div>
      <div className="contribution-item-meta">
        <span>Created: {formatDate(contrib.created_at)}</span>
        {contrib.submitted_at && (
          <span>Submitted: {formatDate(contrib.submitted_at)}</span>
        )}
      </div>
      {contrib.status === 'draft' && onEdit && (
        <button
          className="btn btn-secondary"
          onClick={(e) => {
            e.stopPropagation()
            onEdit(contrib.id)
          }}
          style={{ marginTop: '0.75rem', width: '100%' }}
        >
          Edit Draft
        </button>
      )}
      {contrib.status === 'pending' && (() => {
        const isSiteAdmin = user?.role === 'admin'
        const isOwnContribution = user?.id && (
          user.id === contrib.contributor_id ||
          (contrib.contributor && user.id === contrib.contributor.id)
        )
        const isNewRepository = contrib.submission_type === 'new_repository'
        const isPendingBranchable = (contrib.submission_type === 'new_commit' || contrib.submission_type === 'circuit_notes')
        const isRepoOwner = Boolean(
          user?.id && (
            (contrib.repository_owner_id && user.id === contrib.repository_owner_id) ||
            (contrib.repository_id && managedRepositoryIds?.has(contrib.repository_id))
          )
        )
        const isRepoContrib = ['new_commit', 'circuit_notes', 'new_issue'].includes(contrib.submission_type)
        const canApproveOwnNewRepo = isOwnContribution && isNewRepository
        const canApproveAsRepoOwner = isRepoOwner && isRepoContrib
        const canApprove = isSiteAdmin || canApproveOwnNewRepo || canApproveAsRepoOwner

        return (
          <>
            {isOwnContribution && isPendingBranchable && onEdit && (
              <button
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(contrib.id)
                }}
                style={{ marginTop: '0.75rem', width: '100%' }}
              >
                Update branch
              </button>
            )}
            {canApprove && onApprove && onReject ? (
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onApprove(contrib.id)
                  }}
                  disabled={isProcessing}
                  style={{ flex: 1 }}
                >
                  {canApproveOwnNewRepo ? 'Approve & Publish' : 'Approve'}
                </button>
                {isSiteAdmin && (
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      onReject(contrib)
                    }}
                    disabled={isProcessing}
                    style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545', flex: 1 }}
                  >
                    Reject
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '4px',
                fontSize: '0.9rem',
                color: 'var(--text-muted)',
              }}>
                {isNewRepository
                  ? 'Waiting for site administration approval'
                  : 'Waiting for repository owner or site administration approval'}
              </div>
            )}
          </>
        )
      })()}
      {contrib.status === 'rejected' && contrib.rejection_reason && (
        <div className="contribution-rejection-reason" style={{ marginTop: '0.75rem' }}>
          <strong>Rejection reason:</strong> {contrib.rejection_reason}
        </div>
      )}
      {contrib.status === 'approved' && showApprovedMessage?.[contrib.id] && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          backgroundColor: '#d4edda',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#155724',
        }}>
          Approved! Your contribution has been published.
          {repoSlug && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
              Click anywhere on this card to view the repository →
            </div>
          )}
        </div>
      )}
      {contrib.status === 'approved' && !showApprovedMessage?.[contrib.id] && !repoSlug && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: '#d4edda',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#155724',
          marginTop: '0.75rem',
        }}>
          Approved! Your contribution has been published.
          {contrib.repository_id && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#155724' }}>
              Loading repository link...
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ContributionCard
